import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../../components/Toast/toast.ts";
import axios from "axios";
import AssignTaskModal from "./AssignTaskModal.tsx";
import { useUserContext } from "../../context/UserContext.tsx";
import { DataTable } from "../../components/DataTable/index.ts";
import type { Column } from "../../components/DataTable/index.ts";
import { StatusChip } from "../../components/StatusChip/index.ts";
import DescriptionBox from "../../components/DescriptionBox/index.tsx";
import SearchBar from "./SearchBar.tsx";

const CustomQueryTable: React.FC = ({ customQueryData, setCustomQueryData, isAllCustomQueryDataFetched, setIsAllCustomQueryDataFetched, loading, setLoading }: any) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [currentCount, setCurrentCount] = useState(10);
  const [searchVal, setSearchVal] = useState("");
  const storedToken = localStorage.getItem("token");
  const { globalstorevalue } = useUserContext();

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = dateString.substring(0, dateString.indexOf("T"));
    const time = dateString.substring(dateString.indexOf("T") + 1, dateString.indexOf("Z") - 4);
    const parts = date.split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]} ${time}`;
  };

  const searchData = async () => {
    try {
      if (storedToken) {
        setLoading(true);
        setIsAllCustomQueryDataFetched(false);
        setCurrentCount(10);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/cust-portal/native/custom-query-request?${searchVal ? `search=${searchVal}` : ""}`,
          { headers: { Authorization: `Bearer ${storedToken}` } }
        );
        if (response?.data?.length < 10) setIsAllCustomQueryDataFetched(true);
        setCustomQueryData(response?.data);
        setLoading(false);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "An error occurred.");
      setLoading(false);
    }
  };

  const fetchMoreData = async () => {
    if (isAllCustomQueryDataFetched) return;
    const newRows = currentCount + 20;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/cust-portal/native/custom-query-request?first=0&rows=${newRows}${globalstorevalue ? `&stores=${globalstorevalue}` : ""}${searchVal ? `&search=${searchVal}` : ""}`,
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      const newData = response?.data || [];
      if (newData.length < newRows) setIsAllCustomQueryDataFetched(true);
      setCustomQueryData(newData);
      setCurrentCount(newRows);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || "An error occurred.");
    }
  };

  const handleRowClick = (row: any) => {
    if (row?._id) {
      navigate(`/custom-request/${row._id}`);
      try {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({ event: "store_interaction", storeName: row?.store_name });
      } catch (e) {}
    }
  };

  const columns: Column[] = [
    {
      key: "index",
      label: "S.No",
      width: "5%",
      render: (_: any, __: any, index: number) => (
        <span className="text-[rgb(var(--text-muted))] text-[12px]">{index + 1}</span>
      ),
    },
    {
      key: "store_name",
      label: "Store Name",
      width: "16%",
      sortable: true,
      render: (value: string) => (
        <span className="font-medium text-[rgb(var(--text-primary))] text-[13px]">{value}</span>
      ),
    },
    {
      key: "created_on",
      label: "Created On",
      width: "16%",
      sortable: true,
      render: (value: string) => (
        <span className="text-[12px] text-[rgb(var(--text-secondary))]">{formatDate(value)}</span>
      ),
    },
    {
      key: "custom_query_status",
      label: "Status",
      width: "12%",
      sortable: true,
      render: (value: string) => {
        let variant: "success" | "warning" | "danger" | "info" | "neutral" = "neutral";
        if (value === "Completed" || value === "Sent") variant = "success";
        else if (value === "Pending") variant = "danger";
        const label = value === "Pending" ? "Requested" : value === "Completed" || value === "Sent" ? "Resolved" : value || "N/A";
        return <StatusChip label={label} variant={variant} />;
      },
    },
    {
      key: "custom_query_subject",
      label: "Query Subject",
      width: "22%",
      sortable: true,
      render: (value: string) => (
        <span className="text-[12px] text-[rgb(var(--text-secondary))]">
          {value?.length > 70 ? value.substring(0, 70) + "..." : value || "N/A"}
        </span>
      ),
    },
    {
      key: "custom_query_details",
      label: "Query Details",
      width: "25%",
      sortable: true,
      fullWidthOnMobile: true,
      render: (value: string) => (
        <span className="text-[12px] text-[rgb(var(--text-secondary))]">
          {value?.length > 70 ? value.substring(0, 70) + "..." : value || "N/A"}
        </span>
      ),
    },
  ];

  return (
    <>
      <AssignTaskModal visible={visible} setVisible={setVisible} />
      <DataTable
        columns={columns}
        data={customQueryData || []}
        rowKey="_id"
        loading={loading}
        skeletonRows={5}
        sortable={true}
        emptyMessage="No custom query data available."
        maxHeight="calc(100vh - 380px)"
        onScrollEnd={fetchMoreData}
        hasMore={!isAllCustomQueryDataFetched}
        loader={<div className="flex justify-center py-4"><span className="text-[12px] text-[rgb(var(--text-muted))]">Loading more...</span></div>}
        endMessage={<span>No More Data</span>}
        onRowClick={handleRowClick}
        header={
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <DescriptionBox title="Client Queries" subTitle="Custom Request Queries" description="Manage custom query requests submitted across all locations." />
            <div className="flex items-center gap-2">
              <SearchBar searchVal={searchVal} setSearchVal={setSearchVal} searchData={searchData} />
              <button
                style={{background: "var(--gold-grad)"}}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-gold-3 text-white text-[12px] font-medium transition-colors whitespace-nowrap"
                onClick={() => setVisible(true)}
              >
                + Create New Query
              </button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default CustomQueryTable;
